<template>
  <section class="checkout-summary">
    <div
      v-show="showSummary"
      class="checkout-summary__container"
    >
      <div class="checkout-summary__section">
        <button
          class="checkout-summary__toggle checkout-summary__toggle--open"
          @click.prevent="toggleSummary"
        >
          <span class="screen-reader-text">Sulje yhteenveto</span>
        </button>

        <p class="checkout-summary__heading">
          Tuotteet
          <a
            class="checkout-summary__link"
            href="#tilaus"
          >{{ items == 0 ? $modeoL10n.browse_products : $modeoL10n.edit }}</a>
        </p>
        <div
          class="checkout-summary__text"
          :class="{ 'checkout-summary__text--disabled': items == 0 }"
        >
          <p class="checkout-summary__label">
            Tuotteet yhteensä <span class="checkout-summary__label-detail">({{ items }})</span>
          </p>
          <p class="checkout-summary__value checkout-summary__value--price">
            <price
              :price-without-tax="subTotalWithoutTax"
              :price-with-tax="subTotalWithTax"
            />
          </p>
        </div>
      </div>

      <div class="checkout-summary__section">
        <p class="checkout-summary__heading">
          Asiakas
          <a
            class="checkout-summary__link"
            href="#yhteystiedot"
          >{{ customer ? 'Vaihda' : $modeoL10n.select }}</a>
        </p>
        <div
          class="checkout-summary__text"
          :class="{ 'checkout-summary__text--disabled': !customer }"
        >
          <p v-if="customer">
            <template v-if="customer.billing.company">
              {{ customer.billing.company }}<br>
            </template>
            <template v-if="customer.company_id">
              {{ customer.company_id }}<br>
            </template>
            <template v-if="customer.billing.first_name || customer.billing.last_name">
              {{ customer.billing.first_name }} {{ customer.billing.last_name }}<br>
            </template>
            <template v-if="customer.order_reference">
              Tilausviite: {{ customer.order_reference }}<br>
            </template>
            <template v-if="customer.billing.email">
              {{ customer.billing.email }}<br>
            </template>
            <template v-if="customer.billing.phone">
              {{ customer.billing.phone }}<br>
            </template>
          </p>
          <p v-else>
            Asiakasta ei valittu
          </p>
        </div>
      </div>

      <div class="checkout-summary__section">
        <p class="checkout-summary__heading">
          Tarjouksen tiedot
          <a
            class="checkout-summary__link"
            href="#tarjouksen-tiedot"
          >{{ isOfferDetailsEmpty ? 'Täytä' : 'Muokkaa' }}</a>
        </p>
        <template v-if="!isOfferDetailsEmpty">
          <div class="checkout-summary__text">
            Tarjous WC-{{ offerdetails.id }}
          </div>
          <div
            v-if="offerdetails.customer_reference"
            class="checkout-summary__text"
          >
            {{ offerdetails.customer_reference }}
          </div>
          <div
            v-if="offerdetails.offer_date"
            class="checkout-summary__text"
          >
            <p class="checkout-summary__label">
              Jättöpäivä
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_date }}
            </p>
          </div>
          <div
            v-if="offerdetails.offer_expiry"
            class="checkout-summary__text"
          >
            <p class="checkout-summary__label">
              Voimassaoloaika
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_expiry }}
            </p>
          </div>
          <div
            v-if="offerdetails.status"
            class="checkout-summary__text"
          >
            <p class="checkout-summary__label">
              Tarjouksen tila
            </p>
            <p class="checkout-summary__value">
              {{ statusLabels[offerdetails.status] }}
            </p>
          </div>
          <div
            v-if="offerdetails.offer_delivery"
            class="checkout-summary__text checkout-summary__text--highscreen"
          >
            <p class="checkout-summary__label">
              Kuljetus
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_delivery }}
            </p>
          </div>
          <div
            v-if="offerdetails.offer_installation"
            class="checkout-summary__text checkout-summary__text--highscreen"
          >
            <p class="checkout-summary__label">
              Asennus
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_installation }}
            </p>
          </div>
          <div
            v-if="offerdetails.offer_recycling"
            class="checkout-summary__text checkout-summary__text--highscreen"
          >
            <p class="checkout-summary__label">
              Kierrätys
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_recycling }}
            </p>
          </div>
          <div
            v-if="offerdetails.offer_delivery_time"
            class="checkout-summary__text checkout-summary__text--highscreen"
          >
            <p class="checkout-summary__label">
              Toimitusaika
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_delivery_time }}
            </p>
          </div>
          <div
            v-if="offerdetails.offer_terms"
            class="checkout-summary__text checkout-summary__text--highscreen"
          >
            <p class="checkout-summary__label">
              Toimitusehto
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_terms }}
            </p>
          </div>
          <div
            v-if="offerdetails.offer_payment_terms"
            class="checkout-summary__text checkout-summary__text--highscreen"
          >
            <p class="checkout-summary__label">
              Maksuehto
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_payment_terms }}
            </p>
          </div>
          <div
            v-if="offerdetails.offer_guarantee"
            class="checkout-summary__text checkout-summary__text--highscreen"
          >
            <p class="checkout-summary__label">
              Takuu
            </p>
            <p class="checkout-summary__value">
              {{ offerdetails.offer_guarantee }}
            </p>
          </div>
        </template>
        <p
          v-if="isOfferDetailsEmpty"
          class="checkout-summary__text checkout-summary__text--disabled"
        >
          Tarjouksen tietoja ei syötetty
        </p>
      </div>

      <div class="checkout-summary__section">
        <div class="checkout-summary__text">
          <p class="checkout-summary__label">
            Välisumma
          </p>
          <!-- don't use <price> here -->
          <p class="checkout-summary__value checkout-summary__value--price">
            {{ subTotalWithoutTax | formatPrice }}
          </p>
        </div>
        <div class="checkout-summary__text">
          <p class="checkout-summary__label">
            {{ $modeoL10n.vat }}
          </p>
          <!-- don't use <price> here -->
          <p class="checkout-summary__value checkout-summary__value--price">
            {{ totalTax | formatPrice }}
          </p>
        </div>
        <div class="checkout-summary__total checkout-summary__total--offer">
          <p class="checkout-summary__label">
            Kokonaiskate
          </p>
          <p class="checkout-summary__total__value checkout-summary__value">
            {{ profit | formatPercent }} %
          </p>
        </div>
        <div class="checkout-summary__total checkout-summary__total--offer">
          <p class="checkout-summary__label">
            Yhteensä
          </p>
          <!-- don't use <price> here -->
          <p class="checkout-summary__total__value checkout-summary__value checkout-summary__value--price">
            {{ totalPrice | formatPrice }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="!showSummary"
      class="checkout-summary__mobile-preview"
    >
      <button
        class="checkout-summary__toggle"
        @click.prevent="toggleSummary"
      >
        <span class="screen-reader-text">Näytä yhteenveto</span>
      </button>
      <div class="checkout-summary__total checkout-summary__total--mobile">
        <p class="checkout-summary__label">
          Yhteensä
        </p>
        <!-- don't use <price> here -->
        <p class="checkout-summary__total__value checkout-summary__value checkout-summary__value--price">
          {{ totalPrice | formatPrice }}
        </p>
      </div>
    </div>
  </section>
</template>

<script>
    import _ from 'lodash';

    export default {

        data : function () {
            return {
                showSummary: false,
                statusLabels: {'offer': 'Valmis', 'offer-draft': 'Luonnos'},
            };
        },
        computed: {
            items: function() {
                return this.$store.getters.getTotals.count;
            },
            subtotal: function() {
                return this.subTotalWithoutTax;
            },
            subTotalWithoutTax: function() {
                const totals = this.$store.getters.getTotals;
                return this.parseCommaDecimalFormat( totals.subtotal);
            },
            subTotalWithTax: function() {
                const totals = this.$store.getters.getTotals;
                return this.parseCommaDecimalFormat( totals.subtotal) + this.parseCommaDecimalFormat( totals.subtotalTax );
            },
            totalPrice: function() {
                return this.subTotalWithTax;
            },
            totalTax: function() {
                const totals = this.$store.getters.getTotals;
                return this.parseCommaDecimalFormat( totals.subtotalTax );
            },
            offerdetails: function() {
                return this.$store.getters.getOfferDetails;
            },
            isOfferDetailsEmpty() {
                return _.isEmpty(this.offerdetails);
            },
            profit() {
                let price = this.subTotalWithoutTax;
                let costPrice = this.parseCommaDecimalFormat(this.$store.getters.getTotalCostPrice);

                if (price < 1) {
                    return 0;
                }

                return (price - costPrice) / price * 100;
            },
            customer() {
                return this.$store.getters.getCustomer;
            },
        },

        methods: {
            toggleSummary() {
                this.showSummary = !this.showSummary;
            },
        },
    }
</script>
